/**
 *  @flow
 */
import Box from '@mui/material/Box';
import { useCompanyFeature } from 'utils/useCompanyFeature';
import { Button, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import FormDrawerQuestions from 'components/@home/drawers/FormDrawer/FormDrawerQuestions';
import Headline from 'components/@home/drawers/FormDrawer/Headline';
import QuestionErrorMessage from 'components/@home/drawers/FormDrawer/QuestionErrorMessage';
import InitiativeTagSelector from 'components/@home/@messages/ChatPanel/SendForm/InitiativeTagSelector';
import Title from 'components/@home/modals/acknowledgment/PreviewModal/Form/Title';
import ConfirmDialog from 'components/common/ConfirmDialog';
import Checkbox from 'components/formik/Checkbox';
import TextField from 'components/formik/TextField';
import { Field, FieldArray, Form, useFormikContext } from 'formik';
import isEmpty from 'lodash/isEmpty';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import currentChannel from 'store/selectors/currentChannel';
import isCompanyAdmin from 'store/selectors/isCompanyAdmin';
import canTagInitiative from 'utils/canTagInitiative';
import { I18n } from 'utils/i18n';

type Props = {
  isEdit: boolean,
};

const StyledForm = styled(Form)({
  flexGrow: 1,
  padding: '20px',
  maxWidth: 'calc(100% - 60px)',
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
});

const FormDrawerContent = ({ isEdit }: Props) => {
  const [isOpen, setOpen] = useState(false);
  const areInitiativesEnabled = useCompanyFeature('initiatives');
  const channel = useSelector(currentChannel);
  const isCompanyAdminUser = useSelector(isCompanyAdmin);
  const canAttachInitiative = canTagInitiative(channel, isCompanyAdminUser);
  const formik = useFormikContext();
  const {
    isSubmitting,
    submitCount,
    errors,
    values: { questions, adminOnlyDisabled },
  } = formik;
  const showError = !isEmpty(errors) && submitCount > 0;
  const handleSubmit = async e => {
    e.preventDefault();
    const err = await formik.validateForm();
    if (isEmpty(err)) {
      setOpen(true);
    } else {
      formik.submitForm();
    }
  };
  return (
    <StyledForm onSubmit={handleSubmit}>
      <Headline isEdit={isEdit} />
      <Box sx={{ flexGrow: 1, overflow: 'auto', pb: '20px' }}>
        {questions.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Title>{I18n.t('FormDrawer.Form title')}</Title>
            <Field
              component={TextField}
              sx={{ margin: '0 0 25px', width: '100%' }}
              name="title"
              placeholder={I18n.t('FormDrawer.Form title placeholder')}
            />
          </Box>
        )}
        <FieldArray name="questions" component={FormDrawerQuestions} />
      </Box>
      <Box
        sx={theme => ({
          borderTop: `1px solid ${theme.palette.divider}`,
          pt: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        })}
      >
        <Tooltip title={adminOnlyDisabled ? I18n.t('FormDrawer.Only admins read only') : ''}>
          <div>
            <Field
              name="adminOnly"
              component={Checkbox}
              disabled={adminOnlyDisabled}
              label={I18n.t('FormDrawer.Show answers only to admins')}
            />
          </div>
        </Tooltip>
        <Box sx={{ ml: 4 }}>
          <Field
            name="anonymous"
            component={Checkbox}
            label={I18n.t('FormDrawer.Anonymous answers')}
          />
        </Box>
        {areInitiativesEnabled && canAttachInitiative && !isEdit ? (
          <Box sx={{ ml: 4 }}>
            <InitiativeTagSelector
              value={formik.values.initiativeTag}
              onChange={value => formik.setFieldValue('initiativeTag', value)}
            />
          </Box>
        ) : null}
        <Box sx={{ flex: '1 0 auto', textAlign: 'right', mr: 1 }}>
          {showError ? (
            <QuestionErrorMessage>
              {errors.server || I18n.t('FormDrawer.Please check errors above')}
            </QuestionErrorMessage>
          ) : null}
        </Box>
        <Button
          variant="contained"
          disabled={isSubmitting || showError || isEmpty(questions)}
          color="primary"
          type="submit"
        >
          {isSubmitting
            ? `${
                isEdit ? I18n.t('FormDrawer.Updating form') : I18n.t('FormDrawer.Creating form')
              }...`
            : `${isEdit ? I18n.t('FormDrawer.Update form') : I18n.t('FormDrawer.Create form')}`}
        </Button>
      </Box>
      <ConfirmDialog
        message={I18n.t('FormDrawer.ConfirmAlert.Title')}
        buttons={[
          {
            text: I18n.t('FormDrawer.ConfirmAlert.Confirm'),
            variant: 'contained',
            color: 'primary',
            onClick: () => {
              setOpen(false);
              formik.submitForm();
            },
          },
          { text: I18n.t('FormDrawer.ConfirmAlert.Cancel') },
        ]}
        isOpen={isOpen}
        onClose={() => setOpen(false)}
      >
        <Typography>{I18n.t('FormDrawer.ConfirmAlert.Message')}</Typography>
        {!adminOnlyDisabled && (
          <>
            <br />
            <Field
              name="adminOnly"
              component={Checkbox}
              label={I18n.t('FormDrawer.Show answers only to admins')}
            />
          </>
        )}
        <br />
        <Field
          name="anonymous"
          component={Checkbox}
          label={I18n.t('FormDrawer.Anonymous answers')}
        />
      </ConfirmDialog>
    </StyledForm>
  );
};

export default FormDrawerContent;
